import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";

type Snapshot = {
	orgId: string;
	side: "brand" | "affiliate";
	quality: number;
	speed: number;
	reliability: number;
	compliance: number;
	composite: number;
	provisional: boolean;
	dealsClosed: number;
	computedAt: number;
};

type Event = {
	id: string;
	dimension: "quality" | "speed" | "reliability" | "compliance";
	delta: number;
	sourceKind: string;
	sourceRef: string | null;
	notes: string | null;
	occurredAt: number;
};

type Resp = {
	snapshot: Snapshot | null;
	recentEvents: Event[];
	tier: "provisional" | "emerging" | "full";
};

export function Reputation() {
	const { data, isLoading } = useQuery<Resp>({
		queryKey: ["reputation"],
		queryFn: () => api.get("/reputation"),
	});
	if (isLoading) return <div className="p-6">Loading…</div>;
	if (!data) return null;

	const snap = data.snapshot;
	return (
		<div className="space-y-6 p-6">
			<h1 className="text-xl font-semibold">Reputation</h1>
			{!snap ? (
				<div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm">
					No reputation yet. Close a deal to seed your first events.
				</div>
			) : (
				<>
					<div className="rounded border border-neutral-200 bg-white p-4 shadow-sm">
						<div className="flex items-baseline justify-between">
							<div className="text-2xl font-semibold">{Math.round(snap.composite)}</div>
							<div className="text-xs text-neutral-500">
								tier <span className="font-mono">{data.tier}</span> · {snap.dealsClosed} deal(s)
							</div>
						</div>
						<div className="mt-3 grid grid-cols-4 gap-3">
							<Dim label="Quality" value={snap.quality} weight={40} />
							<Dim label="Speed" value={snap.speed} weight={20} />
							<Dim label="Reliability" value={snap.reliability} weight={20} />
							<Dim label="Compliance" value={snap.compliance} weight={20} />
						</div>
					</div>
					<section>
						<h2 className="mb-2 text-base font-medium">Recent events</h2>
						{data.recentEvents.length === 0 && (
							<div className="text-sm text-neutral-500">No events yet.</div>
						)}
						<ul className="space-y-1 text-sm">
							{data.recentEvents.map((e) => (
								<li
									key={e.id}
									className="flex items-center justify-between rounded border border-neutral-200 p-2"
								>
									<div>
										<span className="font-mono">{e.dimension}</span>
										<span className={e.delta >= 0 ? "ml-2 text-emerald-700" : "ml-2 text-red-700"}>
											{e.delta >= 0 ? "+" : ""}
											{e.delta}
										</span>
										<span className="ml-2 text-xs text-neutral-500">{e.sourceKind}</span>
									</div>
									<div className="text-xs text-neutral-500">
										{new Date(e.occurredAt * 1000).toLocaleString()}
									</div>
								</li>
							))}
						</ul>
					</section>
				</>
			)}
		</div>
	);
}

function Dim(props: { label: string; value: number; weight: number }) {
	return (
		<div className="rounded bg-neutral-50 p-3">
			<div className="text-xs text-neutral-500">
				{props.label} <span className="font-mono">{props.weight}%</span>
			</div>
			<div className="text-xl font-semibold">{Math.round(props.value)}</div>
		</div>
	);
}
